import Highcharts from 'highcharts';
import { Theme } from '../../../../../../../theme/theme';
import { SM_BREAK } from '../RetirementChart.meta';

// Per-chart state so that multiple RetirementChart/ScenarioProjection instances
// mounted in the same document (e.g. multi-section PDF) don't clobber each
// other's labels via shared module-level maps.
const verticalLabelsByChart = new WeakMap<
  Highcharts.Chart,
  Record<string, Highcharts.SVGElement>
>();

const getVerticalLabels = (chart: Highcharts.Chart) => {
  let labels = verticalLabelsByChart.get(chart);
  if (!labels) {
    labels = {};
    verticalLabelsByChart.set(chart, labels);
  }
  return labels;
};

interface RenderVerticalLabelOpts {
  chart: Highcharts.Chart;
  point: Highcharts.Point & { plotX?: number; plotY?: number }; // https://github.com/highcharts/highcharts/issues/13738
  id: string;
  value: number;
  label: string;
  offset: number;
  color: string;
  theme: Theme;
}

export const renderVerticalLabel = ({
  chart,
  point,
  id,
  value,
  label,
  offset,
  color,
  theme,
}: RenderVerticalLabelOpts) => {
  const verticalLabels = getVerticalLabels(chart);

  if (chart.chartWidth < SM_BREAK) {
    Object.values(verticalLabels).forEach(verticalLabel => verticalLabel.destroy());
    verticalLabelsByChart.set(chart, {});

    return;
  }

  if (verticalLabels[id]) {
    verticalLabels[id].destroy();
  }

  verticalLabels[id] = chart.renderer
    .g(id)
    .attr({ zIndex: 3 + (offset === 0 ? 1 : 0) })
    .add();

  const x = chart.plotLeft + (point.plotX || 0);
  const fromY = chart.plotTop + (point.plotY || 0);
  const toY = chart.plotHeight + chart.plotTop + 30 + offset;

  chart.renderer
    .circle(x, fromY, 5)
    .attr({ fill: color })
    .add(verticalLabels[id]);

  const connector = chart.renderer
    .path(
      chart.renderer.crispLine(
        [
          ['M', x, fromY],
          ['L', x, toY],
        ],
        1,
      ),
    )
    .attr({ stroke: color, 'stroke-width': 2, 'stroke-dasharray': '7 3' })
    .add(verticalLabels[id]);

  const labelGroup = chart.renderer.g('label').add(verticalLabels[id]);

  const labelBg = chart.renderer
    .rect(0, 0, 32, 20, 10)
    .attr({ fill: color })
    .align({ align: 'right', verticalAlign: 'bottom', x: -16 }, false, connector.getBBox())
    .add(labelGroup);

  chart.renderer
    .text(value.toString(), 0, 0)
    .attr({ 'text-anchor': 'middle' })
    .css({ color: theme.colors.bg, fontSize: '14px', fontWeight: '600' })
    .align({ align: 'center', verticalAlign: 'middle', y: 5 }, false, labelBg.getBBox())
    .add(labelGroup);

  let labelText = chart.renderer
    .text(label, 0, 0)
    .attr({ 'text-anchor': 'end' })
    .css({ color: theme.colors.neutral['600'], fontSize: '12px', fontWeight: '600' })
    .align({ align: 'left', verticalAlign: 'middle', x: -10 }, false, labelGroup.getBBox())
    .css({ width: 110 })
    .add(verticalLabels[id]);

  // Annotation overflowing on the left side of the chart
  if (labelText.getBBox().x < 0) {
    labelText.destroy();
    labelText = chart.renderer
      .text(label, 0, 0)
      .attr({ 'text-anchor': 'start' })
      .css({ color: theme.colors.neutral['600'], fontSize: '12px', fontWeight: '600' })
      .align({ align: 'right', verticalAlign: 'middle', x: 10 }, false, labelGroup.getBBox())
      .css({ width: 110 })
      .add(verticalLabels[id]);
  }
};

export const removeVerticalLabel = (chart: Highcharts.Chart, id: string) => {
  const verticalLabels = verticalLabelsByChart.get(chart);
  if (verticalLabels?.[id]) {
    verticalLabels[id].destroy();

    delete verticalLabels[id];
  }
};
